import { useState } from 'react';
import { SubscriptionContent } from '@dynamika-shared/types/subscription';
import { SubscriptionFormData } from '../components/AddEditSubscriptionModal';
import { OnSortColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { useClinicalTrialSubscriptionSlice } from '@restore/clinicalTrialSubscription/clinicalTrialSubscription.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useTeamMembersListSlice } from '@restore/teamMembersList/teamMembersList.slice';
import { usePaginationAndSort } from '@restore/helpers';
import { ChannelType, UpdateSubscriptionRequest } from 'src/api/subscriptions';
import {
  getAllowedSiteCodes,
  resolveRecipientScope,
} from '../utils/scopeSites';

export type UseSubscriptionActionsParams = {
  clinicalTrialId: string;
  selectedKey: string | null;
};

export const useSubscriptionActions = ({
  clinicalTrialId,
  selectedKey,
}: UseSubscriptionActionsParams) => {
  const sites = useClinicalTrialSlice(state => state.data?.sites ?? []);
  const teamMembers = useTeamMembersListSlice(state => state.allData);
  const { sortBy, setSortBy } = usePaginationAndSort('subscriptions');
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionContent | undefined>(undefined);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const {
    subscriptions,
    deleteSubscription,
    updateSubscription,
    editSubscriptionById,
    deleteSubscriptionById,
    createSubscription,
  } = useClinicalTrialSubscriptionSlice((state, actions) => ({
    subscriptions: state.subscriptions,
    deleteSubscription: actions.deleteSubscription,
    updateSubscription: actions.updateSubscription,
    editSubscriptionById: actions.editSubscriptionById,
    deleteSubscriptionById: actions.deleteSubscriptionById,
    createSubscription: actions.createSubscription,
  }));

  const handleToggleSubscription = (subscription: SubscriptionContent) => {
    const subscriptionIds = [subscription.subscriptionId];
    if (subscription.subscribed) {
      deleteSubscription({
        subscriptionIds,
        clinicalTrialId,
      });
    } else {
      updateSubscription({
        subscriptionIds,
        clinicalTrialId,
      });
    }
  };

  const handleSubscribeAll = () => {
    const subscriptionIds = subscriptions.content.map(
      sub => sub.subscriptionId
    );
    updateSubscription({
      subscriptionIds,
      clinicalTrialId,
    });
  };

  const handleUnsubscribeAll = () => {
    const subscriptionIds = subscriptions.content.map(
      sub => sub.subscriptionId
    );
    deleteSubscription({
      subscriptionIds,
      clinicalTrialId,
    });
  };

  const onSort: OnSortColumnType = (name, sortState) => {
    const newSortBy = sortState ? `${name},${sortState}` : undefined;
    setSortBy({ listName: 'subscriptions', newSortBy });
  };

  const handleAddSubscription = () => {
    setSelectedSubscription(undefined);
    setIsAddEditModalOpen(true);
  };

  const handleEditSubscription = (subscription: SubscriptionContent) => {
    setSelectedSubscription(subscription);
    setIsAddEditModalOpen(true);
  };

  const handleDeleteSubscription = (subscription: SubscriptionContent) => {
    setSelectedSubscription(subscription);
    setIsDeleteModalOpen(true);
  };

  const handleAction = (action: string, subscription: SubscriptionContent) => {
    if (action === 'edit') {
      handleEditSubscription(subscription);
    } else if (action === 'delete') {
      handleDeleteSubscription(subscription);
    }
  };

  const handleSubmitSubscription = (data: SubscriptionFormData) => {
    const isSystemUser = data.recipientType === 'SYSTEM_USER';

    // Resolve the scope for site-user recipients: a subscription must never be
    // saved with sites the recipient does not belong to, and an empty scope is
    // expanded to the site user's own sites (instead of the "all sites"
    // wildcard) so they are only ever notified about their sites. Unrestricted
    // recipients keep their selection as-is (empty == all sites).
    // NOTE: teamMembers is populated by the modal's own fetch; if it has not
    // loaded yet the recipient resolves to undefined and resolution is a no-op —
    // the modal's dropdown filter is the primary guard, this is defence-in-depth.
    const recipient = isSystemUser
      ? (teamMembers ?? []).find(member => member.id === data.userId)
      : undefined;
    const allowedSiteCodes = getAllowedSiteCodes(recipient);
    const scopedSiteCodes = resolveRecipientScope(data.sites, allowedSiteCodes);

    const selectedSiteIds = scopedSiteCodes
      .map(code => sites.find(site => site.code === code)?.id)
      .filter((id): id is string => Boolean(id));

    const payload = isSystemUser
      ? {
          channelType: 'EMAIL' as ChannelType,
          userId: data.userId,
        }
      : {
          channelType: 'EMAIL' as ChannelType,
          firstName: data.firstName,
          lastName: data.lastName,
        };

    const requestBody: UpdateSubscriptionRequest = {
      ...payload,
      notificationType: selectedKey as string,
      channelUserIdentifier: data.channelUserIdentifier,
      subscribed: true,
      filter: {
        type: 'GENERAL_FILTER',
        owners:
          isSystemUser && data.assignAsOwner ? [data.userId] : [],
        sites: selectedSiteIds,
      },
    };

    if (selectedSubscription) {
      editSubscriptionById({
        subscriptionId: selectedSubscription.subscriptionId,
        clinicalTrialId,
        body: requestBody,
      });
      handleCloseModals();
      setShouldRefetch(true);
    } else {
      createSubscription({
        clinicalTrialId,
        body: requestBody,
      });
      handleCloseModals();
      setShouldRefetch(true);
    }
  };

  const handleConfirmDelete = () => {
    if (selectedSubscription) {
      deleteSubscriptionById({
        subscriptionId: selectedSubscription.subscriptionId,
        clinicalTrialId,
      });
    }

    handleCloseModals();
    setShouldRefetch(true);
  };

  const handleCloseModals = () => {
    setIsAddEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedSubscription(undefined);
  };

  const handlePaginationChange = () => {
    setShouldRefetch(true);
  };

  return {
    sortBy,
    isAddEditModalOpen,
    isDeleteModalOpen,
    selectedSubscription,
    shouldRefetch,
    setShouldRefetch,
    handleToggleSubscription,
    handleSubscribeAll,
    handleUnsubscribeAll,
    onSort,
    handleAddSubscription,
    handleAction,
    handleSubmitSubscription,
    handleConfirmDelete,
    handleCloseModals,
    handlePaginationChange,
  };
};
